import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore, leaveToDashboard } from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Restores an existing session on load. A transient network error must not
    // log the user out — only a definitive 401 does. Retry once on other errors.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const r = await api.get("/auth/me");
        setUser(r.data);
        return r.data;
      } catch (e) {
        if (e?.response?.status === 401) break;
        if (attempt === 0) await new Promise((res) => setTimeout(res, 700));
      }
    }
    setUser(null);
    return null;
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Public routes — no auth fetch needed
    const publicPrefixes = ["/pulse/"];
    if (publicPrefixes.some((p) => window.location.pathname.startsWith(p))) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const loginJwt = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    if (r.data.mfa_required) return r.data; // { mfa_required: true, mfa_token }
    setUser(r.data.user);
    return r.data.user;
  };

  const verifyMfaLogin = async (mfaToken, code) => {
    const r = await api.post("/auth/mfa/login-verify", { mfa_token: mfaToken, code });
    setUser(r.data.user);
    return r.data.user;
  };

  const registerJwt = async (payload) => {
    const r = await api.post("/auth/register", payload);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    leaveToDashboard();
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, loginJwt, verifyMfaLogin, registerJwt, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
